// src/components/GrowthOpportunitiesSection.tsx
import React from 'react';
import { TreeDeciduous, ChevronDown } from 'lucide-react';
import { cn } from '../utils/cn';

export default function GrowthOpportunitiesSection({
  open,
  toggle,
  sectionKey,
  progress,
  toggleItem
}) {
 const growthChecklistByStage = [
    {
      stage: 'Stage 1: Get Grounded',
      items: [
        { id: 'journal_entry', text: 'Start your first journal entry' },
        { id: 'lesson_learned', text: 'Complete your first lesson learned' },
        { id: 'meaningful_action', text: 'Take your first meaningful action' },
        { id: 'one_week_use', text: 'Use the app daily for one week' },
        { id: 'journal_resistance', text: 'Do the journaling even when you really don’t feel like it' }
      ]
    },
    {
      stage: 'Stage 2: Create and Share',
      items: [
        { id: 'download_gold_file', text: 'Download your first Gold File' },
        { id: 'blog_post', text: 'Create a blog post using your Gold File' },
        { id: 'social_post', text: 'Create a social media post using your Gold File' },
        { id: 'speech_prep', text: 'Use your Gold File to prepare for a speech, talk, or meeting' }
      ]
    },
    {
      stage: 'Stage 3: Vision and Design',
      items: [
        { id: 'book_title', text: 'Identify a book title using your Gold File' },
        { id: 'course_title', text: 'Identify a course or program using your Gold File' },
        { id: 'build_program', text: 'Use the Gold File to flesh out a book, program, or course' },
        { id: 'impromptu', text: 'Use your Gold File to generate ideas for an impromptu opportunity' }
      ]
    },
    {
      stage: 'Stage 4: Reflect and Recognize',
      items: [
        { id: 'recovery', text: 'Recover after missing a day and identify a lesson learned' },
        { id: 'discuss_values', text: 'Discuss your core values with someone' },
        { id: 'midday_problem', text: 'Journal midday to solve a problem' },
        { id: 'solve_personal', text: 'Use your Gold File to solve a personal problem' },
        { id: 'solve_professional', text: 'Use your Gold File to solve a professional problem' }
      ]
    },
    {
      stage: 'Stage 5: Live and Expand',
      items: [
        { id: 'vacation_ideas', text: 'Use your Gold File to create ideas for a vacation, adventure, or outing' },
        { id: 'joy_experience', text: 'Use your Gold File to design experiences that bring joy and connection' },
        { id: 'update_dating', text: 'Use your Gold File to update your dating profile or improve your relationship' },
        { id: 'deepen_connection', text: 'Use your Gold File to deepen connections with friends and community' }
      ]
    }
  ];

   const stageColors = {
    'Stage 1: Get Grounded': 'bg-blue-50',
    'Stage 2: Create and Share': 'bg-green-50',
    'Stage 3: Vision and Design': 'bg-yellow-50',
    'Stage 4: Reflect and Recognize': 'bg-purple-50',
    'Stage 5: Live and Expand': 'bg-orange-50'
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
      <button
        onClick={() => toggle(sectionKey)}
        className="w-full flex justify-between items-center px-4 py-6 text-left hover:bg-blue-50"
      >
        <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
          <TreeDeciduous className="w-6 h-6 text-[#236caf]" />
          Growth Checklist
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-[#236caf] transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>
      {open && (
        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-gray-600 text-sm">
            If you value structure, set a goal to check off these items. You'll grow personally and professionally every time you do.
          </p>
          {growthChecklistByStage.map(stageGroup => (
            <div key={stageGroup.stage}>
              <h3 className="text-md font-semibold text-[#004060] mb-2 mt-4">
                {stageGroup.stage}
              </h3>
              <ul className="space-y-2">
                {stageGroup.items.map(item => (
                  <li
                    key={item.id}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-4 py-2',
                      stageColors[stageGroup.stage]
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={!!progress[item.id]}
                      onChange={() => toggleItem(item.id)}
                      className="form-checkbox h-5 w-5 text-[#236caf] rounded border-gray-300"
                    />
                    <span className={`text-sm ${progress[item.id] ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                      {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
